import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Box, Tooltip, Typography, useMediaQuery, useTheme, Stack } from '@mui/material'
import {
    DataGridPro,
    GridCallbackDetails,
    gridClasses,
    GridColDef,
    GridColumnOrderChangeParams,
    GridColumnVisibilityModel,
    GridFilterModel,
    GridRenderCellParams,
    GridSlots,
    GridSortModel,
} from '@mui/x-data-grid-pro'
import { enUS, svSE } from '@mui/x-data-grid-pro/locales'
import * as datefns from 'date-fns'
import { ExtractAtomValue, useAtom } from 'jotai'
import {
    advisorSettings,
    issueFiltersAtom,
    issueGridCurrentPageAtom,
    issueSearchTextfieldAtom,
    issueTableDataAtom,
    priceChangeSettings,
} from '../../atoms/issueAtom'
import {
    formatBigMoney,
    formatDateYYYYMMDD,
    formatMoney,
    formatPercentage,
} from '../../packages/utils/numberFormatters'
import { IssueStatusDescription } from './IssueStatus'
import { IssueTypeText } from './IssueType'
import OutcomeChart from './OutcomeChart'
import IssueTypeLabels from './IssueTypeLabels'
import { CommitChart } from './CommitChart'
import { isPreviousIssue } from '../utils/categorizeIssues'
import {
    calculateDilution,
    calculateDiscount,
    calculatePriceChange,
    getIssueSubscribedSize,
} from '../../pages/issues/utils'
import { ColoredPercentage } from './utils/ColoredPercentage'
import { PopoverSelector } from './PopoverSelector'
import { TableSettingHeader, TableSettingHeaderProps } from './TableSettingHeader'
import { CustomDataGridToolbar, CustomDataGridToolbarProps } from '../CustomDataGridToolbar'
import { FullHeightBox } from '../UIComponents'
import { createNoteIndicatorColumn, noteIndicatorGridSx } from './NoteRowIndicator'
import { useEntityNoteIds } from '../../hooks/useEntityNoteIds'
import { EnrichedIssueEntity, IssueEntity } from 'types/IssueEntity'
import { getSubPriceLabel } from '../../utils/issues/getSubPriceLabel'
import { WarrantName } from './WarrantName'
import { WarrantEntity } from 'types/WarrantEntity'

const ISSUE_TYPES_WITH_COMMIT_CHART = ['Rights issue', 'IPO']
const ISSUE_TYPES_WITH_OUTCOME_CHART = ['Rights issue', 'IPO']

export type IssueGridCols =
    | 'issueLabel'
    | 'issueType'
    | 'shareName'
    | 'tags'
    | 'status'
    | 'date'
    | 'offeredSize'
    | 'subscribedSize'
    | 'financialAdvisors'
    | 'totalCommitment'
    | 'outcome'
    | 'priceChange'
    | 'updatedAt'
    | 'noteIndicator'
    | 'sector'
    | 'marketplace'
    | 'preMoney'
    | 'subscriptionPrice'
    | 'closePrice'
    | 'turnover10d'
    | 'marketcap'
    | 'legalAdvisors'
    | 'issuingAgents'
    | 'discount10dVwap'
    | 'terpDiscount10dVwap'
    | 'dilutionMax'
    | 'dilutionActual'
    | 'feeLevel0'
    | 'warrants'
    | 'noncashConsideration'

const issuesCols = (
    tableSettings: ExtractAtomValue<typeof issueTableDataAtom>,
    onOpenTableSetting: TableSettingHeaderProps['onClick'],
    locale: string,
    t: (v: string) => string,
    isCompact: boolean,
    detailsPath: (v: IssueEntity) => string,
    entityIdsWithNotes: Set<string> | null,
    reloadEntityNoteIds: () => void,
) => {
    const LinkWrapper = ({ children, to }: { children?: ReactNode | null; to: string }) => (
        <Link
            to={to}
            style={{
                display: 'flex',
                width: '100%',
                height: '100%',
                textDecoration: 'none',
                color: '#000',
                justifyContent: 'inherit',
            }}
        >
            {children}
        </Link>
    )

    const cols: Array<GridColDef<EnrichedIssueEntity> & { field: IssueGridCols }> = [
        {
            field: 'issueLabel',
            headerName: 'Emission',
            headerAlign: 'left',
            minWidth: isCompact ? undefined : 400,
            flex: isCompact ? 2 : 1,
            align: 'left',
            valueGetter: (_, row) => row.shareName,
            renderCell: (params) => {
                return (
                    <LinkWrapper to={detailsPath(params.row)}>
                        <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            spacing={2}
                            sx={{
                                alignItems: { xs: 'start', md: 'center' },
                                paddingY: { xs: 2, md: 0 },
                            }}
                        >
                            <Stack sx={{ justifyContent: 'center', minWidth: '90px' }}>
                                <IssueTypeLabels issue={params.row} />
                            </Stack>
                            <Stack>{params.row.shareName}</Stack>
                        </Stack>
                    </LinkWrapper>
                )
            },
        },
        {
            field: 'marketplace',
            headerName: 'Lista',
            valueGetter: (val, issue) => issue.marketplaceName,
        },
        {
            field: 'preMoney',
            headerName: 'Pre-money',
            valueFormatter: (val, issue) => (val ? formatBigMoney(val, { currencySymbol: issue.currency }) : '-'),
        },
        {
            field: 'subscriptionPrice',
            headerName: 'Teckningskurs',
            valueGetter: (val, issue) => getSubPriceLabel({ issue, locale: 'sv', showUnitToShareConversion: false }),
        },
        {
            field: 'closePrice',
            headerName: 'Aktiekurs (igår)',
            valueGetter: (val, issue) => {
                return formatMoney(issue.closePrice, {
                    currencySymbol: issue.currency,
                    minDecimals: 2,
                    maxDecimals: 5,
                    locale: 'sv',
                })
            },
        },
        {
            field: 'turnover10d',
            headerName: 'Oms / dag (10d)',
            valueGetter: () => null,
        },
        {
            field: 'marketcap',
            headerName: 'Börsvärde (igår)',
            valueFormatter: (value, issue) => formatBigMoney(value, { currencySymbol: issue.currency, locale: 'sv' }),
        },
        {
            field: 'legalAdvisors',
            headerName: 'Legal rådgivare',
            valueGetter: (val, issue) => issue.legalAdvisors.map((la) => la.name).join(', '),
        },
        {
            field: 'issuingAgents',
            headerName: 'Emissionsinstitut',
            valueGetter: (val, issue) => issue.issuingAgents?.map((ia) => ia.name).join(', '),
        },
        {
            field: 'discount10dVwap',
            headerName: 'Rabatt (10d VWAP)',
            valueGetter: (val, issue) => {
                const { vwap } =
                    issue.tradesStats.find(
                        ({ periodType, numberOfDays }) => periodType === 'prePublication' && numberOfDays === 10,
                    ) ?? {}

                if (!vwap) return null

                return calculateDiscount(issue, vwap, false)
            },
            valueFormatter: (val) => formatPercentage(val, { locale: 'sv' }),
        },
        {
            field: 'terpDiscount10dVwap',
            headerName: 'TERP-Rabatt (10d VWAP)',
            valueGetter: (val, issue) => {
                const { vwap } =
                    issue.tradesStats.find(
                        ({ periodType, numberOfDays }) => periodType === 'prePublication' && numberOfDays === 10,
                    ) ?? {}

                if (!vwap) return null

                return calculateDiscount(issue, vwap, true)
            },
            valueFormatter: (val) => formatPercentage(val, { locale: 'sv' }),
        },
        {
            field: 'dilutionMax',
            headerName: 'Utspädning (max)',
            valueGetter: (val, issue) => calculateDilution(issue).maxDilution,
            valueFormatter: (val) => formatPercentage(val, { locale: 'sv' }),
        },
        {
            field: 'dilutionActual',
            headerName: 'Utspädning (faktisk)',
            valueGetter: (val, issue) => calculateDilution(issue).actualDilution,
            valueFormatter: (val) => formatPercentage(val, { locale: 'sv' }),
        },
        {
            field: 'feeLevel0',
            headerName: 'Garantiersättning',
            valueGetter: () => null,
        },
        {
            field: 'warrants',
            headerName: 'Teckningsoptioner',
            sortComparator: (a, b) => a.length - b.length,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            renderCell: (params: GridRenderCellParams<any, WarrantEntity[]>) =>
                params.value?.map((warrant) => (
                    <div key={warrant.id}>
                        <Link to={`/warrants/${warrant.id}`}>
                            <WarrantName warrant={warrant} />
                        </Link>
                    </div>
                )),
        },
        {
            field: 'noncashConsideration',
            headerName: 'Apportegendom',
        },
        {
            field: 'issueType',
            headerName: 'Typ',
            headerAlign: 'left',
            minWidth: 160,
            maxWidth: 400,
            flex: 1,
            align: 'left',
            valueGetter: (_, row) => t(`entities.issueType.values.${row.type}`), // Display the type
            sortComparator: (v1, v2) => v1.localeCompare(v2), // Custom sorting based on the 'type' property
            renderCell: (params) => (
                <Tooltip title='Visa detaljer om emissionen'>
                    <LinkWrapper to={detailsPath(params.row)}>
                        <IssueTypeText issueType={params.row.type} />
                    </LinkWrapper>
                </Tooltip>
            ),
        },
        {
            field: 'shareName',
            headerName: 'Aktie',
            headerAlign: 'left',
            minWidth: 180,
            maxWidth: 400,
            flex: 1,
            align: 'left',
            renderCell: (params) => (
                <Tooltip title='Visa detaljer om bolaget'>
                    <LinkWrapper to={`/companies/${params.row.companyId}`}>
                        <Link to={`/companies/${params.row.companyId}`}>
                            {params.row.shareName || params.row.shareId}
                        </Link>
                    </LinkWrapper>
                </Tooltip>
            ),
        },
        {
            field: 'tags',
            headerName: 'Taggar',
            headerAlign: 'left',
            flex: 1,
            minWidth: 90,
            sortable: false,
            display: 'flex',
            align: 'left',
            renderCell: (params) => (
                <LinkWrapper to={detailsPath(params.row)}>
                    <IssueTypeLabels issue={params.row} />
                </LinkWrapper>
            ),
        },
        {
            field: 'status',
            headerName: 'Status',
            headerAlign: 'left',
            flex: 1,
            align: 'left',
            valueGetter: (_, row) => {
                // lower number will be displayed first in the grid
                if (row.statusComplete === null) return '9' // sort last for now
                const hideUntil =
                    row.statusHideUntil && new Date(row.statusHideUntil) > new Date() ? row.statusHideUntil : null
                if (hideUntil) {
                    return `2-${formatDateYYYYMMDD(hideUntil)}` // just before the "all done" status
                }
                if (row.statusComplete) {
                    return row.statusStage && row.statusStage >= 5 ? '5' : '4'
                } else {
                    return '1'
                }
            },
            display: 'flex',
            renderCell: (params) => (
                <LinkWrapper to={detailsPath(params.row)}>
                    <FullHeightBox>
                        <IssueStatusDescription
                            shortForm
                            hideUntil={params.row.statusHideUntil}
                            stage={params.row.statusStage}
                            isComplete={params.row.statusComplete}
                        />
                    </FullHeightBox>
                </LinkWrapper>
            ),
        },
        {
            field: 'date',
            headerName: 'Datum',
            headerAlign: 'left',
            description:
                'Datumet visar sista dagen i teckningsperioden för avslutade företrädesemissioner och första handelsdagen för genomförda IPOer, annars hela teckningsperioden. För övriga emissioner är datumet när emissionen först offentliggjordes.',
            align: 'left',
            flex: 1.2,
            minWidth: 120,
            maxWidth: 300,
            width: 250,
            valueGetter: (_, row) => {
                if (row.type === 'IPO') {
                    return isPreviousIssue(row) ? row.ipoTradingDate : row.subEndDate
                }
                return row.type === 'Rights issue' ? row.subEndDate : row.publicationDate
            },
            sortComparator: (v1, v2) => {
                const date1 = new Date(v1).getTime()
                const date2 = new Date(v2).getTime()
                return date1 - date2
            },
            renderCell: (params) => {
                const isIpoOrRights = ['IPO', 'Rights issue'].includes(params.row.type)

                return (
                    <LinkWrapper to={detailsPath(params.row)}>
                        <FullHeightBox>
                            <Typography sx={{ whiteSpace: 'nowrap' }}>
                                {params.row.type === 'Rights issue' && isPreviousIssue(params.row)
                                    ? formatDateYYYYMMDD(params.row.subEndDate)
                                    : null}
                                {params.row.type === 'IPO' && isPreviousIssue(params.row)
                                    ? formatDateYYYYMMDD(params.row.ipoTradingDate)
                                    : null}
                                {isIpoOrRights &&
                                    !isPreviousIssue(params.row) &&
                                    `${formatDateYYYYMMDD(params.row.subStartDate)} - ${formatDateYYYYMMDD(
                                        params.row.subEndDate,
                                    )}`}
                                {!isIpoOrRights && formatDateYYYYMMDD(params.row.publicationDate)}
                            </Typography>
                        </FullHeightBox>
                    </LinkWrapper>
                )
            },
        },
        {
            field: 'sector',
            headerName: 'Branch',
            valueGetter: (value, issue) => {
                return issue.sectorLevel1
            },
        },
        {
            field: 'offeredSize',
            headerName: 'Belopp',
            headerAlign: 'left',
            align: 'left',
            minWidth: 70,
            flex: 1,
            valueGetter: (value, issue) => {
                return issue.totalUnitSize
            },
            renderCell: (params) => (
                <LinkWrapper to={detailsPath(params.row)}>
                    <FullHeightBox>
                        {params.value != null
                            ? formatBigMoney(params.value, { locale, currencySymbol: params.row.currency })
                            : null}
                    </FullHeightBox>
                </LinkWrapper>
            ),
        },
        {
            field: 'subscribedSize',
            headerName: 'Tecknat belopp',
            headerAlign: 'left',
            align: 'left',
            flex: 1,
            valueGetter: (value, issue) => {
                return getIssueSubscribedSize(issue)
            },
            renderCell: (params) => (
                <LinkWrapper to={detailsPath(params.row)}>
                    <FullHeightBox>
                        {params.value != null
                            ? formatBigMoney(params.value, { locale, currencySymbol: params.row.currency })
                            : null}
                    </FullHeightBox>
                </LinkWrapper>
            ),
        },
        {
            field: 'financialAdvisors',
            headerName: 'Fin. rådgivare',
            headerAlign: 'left',
            align: 'left',
            minWidth: 120,
            flex: 1,
            disableReorder: false,
            sortable: true,
            renderHeader: () => {
                const header = advisorSettings.find(({ id }) => id === tableSettings.advisors.value[0])?.label

                return <TableSettingHeader header={header} onClick={onOpenTableSetting} setting='advisors' />
            },
            renderCell: (params) => {
                const advisorKey = tableSettings.advisors.value[0]
                const advisors = params.row[advisorKey]
                const count = advisors?.length

                if (!count) {
                    return <LinkWrapper to={detailsPath(params.row)} />
                }

                const suffix = count > 1 ? ` +${count - 1}` : ''
                return (
                    <LinkWrapper to={detailsPath(params.row)}>
                        <div style={{ whiteSpace: 'nowrap' }}>
                            {advisors[0].name} {suffix}
                        </div>
                    </LinkWrapper>
                )
            },
        },
        {
            field: 'totalCommitment',
            headerName: 'Säkerställandegrad',
            headerAlign: 'center',
            align: 'center',
            minWidth: 200,
            flex: 1,
            cellClassName: 'align-content--center',
            renderCell: (params) =>
                ISSUE_TYPES_WITH_COMMIT_CHART.includes(params.row.type) ? (
                    <LinkWrapper to={detailsPath(params.row)}>
                        <FullHeightBox>
                            <CommitChart issue={params.row} size='small' />
                        </FullHeightBox>
                    </LinkWrapper>
                ) : null,
        },
        {
            field: 'outcome',
            headerName: 'Utfall',
            headerAlign: 'center',
            align: 'center',
            minWidth: 200,
            flex: 1,
            sortable: false,
            cellClassName: 'align-content--center',
            renderCell: (params) =>
                ISSUE_TYPES_WITH_OUTCOME_CHART.includes(params.row.type) ? (
                    <LinkWrapper to={detailsPath(params.row)}>
                        <FullHeightBox>
                            <OutcomeChart issue={params.row} />
                        </FullHeightBox>
                    </LinkWrapper>
                ) : (
                    <LinkWrapper to={detailsPath(params.row)} />
                ),
        },
        {
            field: 'priceChange',
            headerName: 'Kursutveckling',
            headerAlign: 'center',
            align: 'center',
            minWidth: 170,
            flex: 1,
            renderHeader: () => (
                <TableSettingHeader header='Kursutveckling' onClick={onOpenTableSetting} setting='priceChange' />
            ),
            sortable: true,
            valueGetter: (params, issue) => {
                const priceChangeFilterValue = tableSettings.priceChange?.value[0]
                const tradeStats = issue.tradesStats.find(({ periodType }) => periodType === priceChangeFilterValue)

                const priceChange = calculatePriceChange(tradeStats, issue.subscriptionRate)
                return priceChange
            },
            renderCell: ({ formattedValue, ...params }) => {
                if (formattedValue === null) return <LinkWrapper to={detailsPath(params.row)}>'-'</LinkWrapper>

                return (
                    <Tooltip title='Kursutvecklingen avser förändringen från teckningskursen till dagens aktiekurs, eller aktiekursen vid vald tidsperiod.'>
                        <LinkWrapper to={detailsPath(params.row)}>
                            <div>
                                <ColoredPercentage value={formattedValue} />
                            </div>
                        </LinkWrapper>
                    </Tooltip>
                )
            },
        },
        {
            field: 'updatedAt',
            headerName: 'Uppdaterad',
            headerAlign: 'left',
            align: 'left',
            flex: 1,
            valueFormatter: (value) => datefns.format(new Date(value), 'yyyy-MM-dd HH:mm:ss'),
        },
    ]

    if (entityIdsWithNotes) {
        const noteIndicator = createNoteIndicatorColumn(entityIdsWithNotes, 'issue', reloadEntityNoteIds)
        cols.push(noteIndicator)
    }

    return cols
}

const gridTextI18n = (locale: string) => {
    if (locale?.startsWith('sv')) {
        return svSE.components.MuiDataGrid.defaultProps.localeText
    } else {
        return enUS.components.MuiDataGrid.defaultProps.localeText
    }
}

function CustomNoResultsOverlay() {
    return (
        <Box sx={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant='body1'>Inga emissioner hittades</Typography>
        </Box>
    )
}

function IssuesGrid({
    availableColumns,
    columnOrder,
    isCompact = false,
    isLoading,
    issues,
    onColumnOrderChange,
    onColumnVisibilityChange,
    sortModel = null,
    pagination = true,
    pageSizeInitialState = 10,
    pageSizeOptions = [10, 25, 50, 100],
    hideFooter = false,
    visibleColumns,
    hasSearchFilter = false,
    detailsPath = null,
}: {
    availableColumns: IssueGridCols[]
    columnOrder?: IssueGridCols[]
    visibleColumns?: Partial<Record<IssueGridCols, boolean>>
    onColumnOrderChange?: (settings: Array<IssueGridCols>) => void
    onColumnVisibilityChange?: (settings: Partial<Record<IssueGridCols, boolean>>) => void
    isCompact?: boolean
    isLoading?: boolean
    issues: EnrichedIssueEntity[]
    sortModel?: GridSortModel | null
    pagination?: boolean
    pageSizeInitialState?: number
    pageSizeOptions?: number[]
    hideFooter?: boolean
    hasSearchFilter?: boolean
    detailsPath?: ((v: IssueEntity) => string) | null
}) {
    const [popoverAnchorEl, setPopoverAnchorEl] = useState<Element | null>(null)
    const { t, i18n } = useTranslation()
    const locale = i18n.language

    const [issueGridCurrentPage, setIssueGridCurrentPage] = useAtom(issueGridCurrentPageAtom)
    const [searchTextfield, setSearchTextfield] = useAtom<string>(issueSearchTextfieldAtom)
    const [filters] = useAtom(issueFiltersAtom)
    const [tableSettings, setTableSettings] = useAtom(issueTableDataAtom)
    const [tableSettingInEdit, setTableSettingInEdit] = useState<'advisors' | 'priceChange' | null>(null)
    const [pageSize, setPageSize] = useState(pageSizeInitialState)
    const { entityIdsWithNotes, reloadEntityNoteIds } = useEntityNoteIds()

    const onOpenTableSetting = useCallback((element: Element, setting: 'advisors' | 'priceChange') => {
        setPopoverAnchorEl(element)
        setTableSettingInEdit(setting)
    }, [])

    detailsPath = detailsPath || ((issue) => `/issues/${issue.id}`)

    const columns = useMemo(
        () =>
            issuesCols(
                tableSettings,
                onOpenTableSetting,
                locale,
                t,
                isCompact,
                detailsPath,
                entityIdsWithNotes,
                reloadEntityNoteIds,
            )
                .filter((col) => availableColumns.includes(col.field))
                .sort((a, b) => {
                    if (!columnOrder) return 0

                    return columnOrder.findIndex((v) => a.field === v) - columnOrder.findIndex((v) => b.field === v)
                }),
        [
            availableColumns,
            columnOrder,
            detailsPath,
            entityIdsWithNotes,
            isCompact,
            locale,
            onOpenTableSetting,
            reloadEntityNoteIds,
            t,
            tableSettings,
        ],
    )

    const columnVisibilityModel = useMemo(
        () =>
            availableColumns.reduce((acc, col) => {
                return {
                    ...acc,
                    [col]: !visibleColumns ? true : (visibleColumns[col] ?? false),
                }
            }, {}),
        [availableColumns, visibleColumns],
    )

    // Initialize filterModel with quickFilterValues and an empty items array
    const [filterModel, setFilterModel] = useState<GridFilterModel>({
        items: [], // Ensure items is an empty array initially
        quickFilterValues: [hasSearchFilter ? searchTextfield : ''], // Default quick filter value
    })

    const theme = useTheme()
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'))

    useEffect(() => {
        setIssueGridCurrentPage(0)
    }, [setIssueGridCurrentPage, filters])

    const handleFilterModelChange = (newFilterModel: GridFilterModel) => {
        setFilterModel({
            ...newFilterModel,
            items: newFilterModel.items || [], // Ensure items is always an array
        })
        setIssueGridCurrentPage(0)
        setSearchTextfield(newFilterModel.quickFilterValues?.[0])
    }

    const disableSelectedSellOutlineStyle = {
        [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
            outline: 'none',
        },
        [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: {
            outline: 'none',
        },
    }

    const handleTableSettingChange = useCallback(
        async (settingName: 'advisors' | 'priceChange', selectedItems: string[]) => {
            setTableSettings((prevFilters) => ({
                ...prevFilters,
                [settingName]: { value: selectedItems },
            }))
        },
        [setTableSettings],
    )

    const handleColumnVisibilityChange = (model: GridColumnVisibilityModel) => {
        onColumnVisibilityChange?.(model as Partial<Record<IssueGridCols, boolean>>)
    }
    const handleColumnOrderChange = (
        params: GridColumnOrderChangeParams,
        event: unknown,
        details: GridCallbackDetails,
    ) => {
        onColumnOrderChange?.(details.api.getAllColumns().map((c) => c.field) as IssueGridCols[])
    }
    const getRowHeight = React.useCallback(() => (isSmallScreen ? 'auto' : 'auto'), [isSmallScreen])

    const grid = (
        // adapt filter: https://mui.com/x/react-data-grid/filtering/customization/#create-a-custom-operator
        <>
            {popoverAnchorEl && tableSettingInEdit === 'priceChange' && (
                <PopoverSelector
                    anchorEl={popoverAnchorEl}
                    label='Välj tidsperiod sedan emisson'
                    onClose={() => setPopoverAnchorEl(null)}
                    onChange={(selectedItems) => void handleTableSettingChange('priceChange', selectedItems)}
                    options={priceChangeSettings}
                    selectedId={tableSettings.priceChange.value}
                />
            )}
            {popoverAnchorEl && tableSettingInEdit === 'advisors' && (
                <PopoverSelector
                    anchorEl={popoverAnchorEl}
                    label='Välj typ av rådgivare att visa'
                    onClose={() => setPopoverAnchorEl(null)}
                    onChange={(selectedItems) => void handleTableSettingChange('advisors', selectedItems)}
                    options={advisorSettings}
                    selectedId={tableSettings.advisors.value}
                />
            )}
            <DataGridPro
                sx={{
                    ...disableSelectedSellOutlineStyle,
                    '& .MuiDataGrid-main': { width: 0, minWidth: '100%' }, // https://github.com/mui/mui-x/issues/8168#issuecomment-1465784733
                    width: '100%',
                    '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { py: '8px' },
                    '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { py: '15px' },
                    '& .even': {
                        backgroundColor: theme.palette.rowColor.main, // Use palette color for even rows
                    },
                    '& .odd': {
                        // backgroundColor: theme.palette.rowColor.light, // Use palette color for odd rows
                    },
                    '& .MuiDataGrid-row': {
                        cursor: 'pointer',
                    },
                    '.align-content--center': {
                        alignContent: 'center',
                    },
                    ...noteIndicatorGridSx,
                }}
                columnVisibilityModel={columnVisibilityModel}
                onColumnOrderChange={handleColumnOrderChange}
                onColumnVisibilityModelChange={handleColumnVisibilityChange}
                sortingOrder={['asc', 'desc']}
                loading={isLoading}
                localeText={gridTextI18n(locale)}
                pagination={pagination}
                initialState={{
                    sorting: { sortModel: sortModel ?? undefined },
                }}
                pageSizeOptions={pageSizeOptions}
                getRowHeight={getRowHeight}
                disableColumnMenu
                hideFooter={hideFooter}
                disableRowSelectionOnClick
                rows={issues}
                columns={columns}
                density={isCompact ? 'compact' : 'standard'}
                slots={{
                    toolbar: CustomDataGridToolbar as GridSlots['toolbar'],
                    noResultsOverlay: CustomNoResultsOverlay,
                    noRowsOverlay: CustomNoResultsOverlay,
                }}
                slotProps={{
                    loadingOverlay: {
                        variant: 'skeleton',
                        noRowsVariant: 'skeleton',
                    },
                    toolbar: {
                        showSearchFilter: hasSearchFilter,
                    } as CustomDataGridToolbarProps,
                }}
                showToolbar={!!onColumnVisibilityChange || hasSearchFilter}
                getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd')}
                paginationModel={{ pageSize, page: issueGridCurrentPage }}
                onPaginationModelChange={({ page, pageSize: pSize }) => {
                    setIssueGridCurrentPage(page)
                    setPageSize(pSize)
                }}
                filterModel={filterModel}
                onFilterModelChange={handleFilterModelChange} // Capture changes to the filter model
            />
        </>
    )
    return grid
}

export default IssuesGrid
